import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { InvoiceSettings } from '@/types/invoice';
import { useToast } from '@/hooks/use-toast';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { logger } from '@/utils/logger';

export const useInvoiceSettings = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { companyId, isContextReady } = useMultiTenant();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['invoice-settings', companyId],
    enabled: isContextReady && !!companyId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoice_settings')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      return data as InvoiceSettings | null;
    },
  });

  const updateSettings = useMutation({
    mutationFn: async (updates: Partial<InvoiceSettings>) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('Usuário não autenticado');

      // Get current company
      const { data: profile } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.company_id) throw new Error('Empresa não encontrada');

      const settingsData = {
        ...updates,
        company_id: profile.company_id,
        updated_by: user.id,
      };

      let savedData;

      if (settings?.id) {
        // Update existing settings
        const { data, error } = await supabase
          .from('invoice_settings')
          .update(settingsData)
          .eq('id', settings.id)
          .select()
          .single();

        if (error) throw error;
        savedData = data;
      } else {
        // Create new settings
        const { data, error } = await supabase
          .from('invoice_settings')
          .insert(settingsData)
          .select()
          .single();

        if (error) throw error;
        savedData = data;
      }

      // §I.142 Fase 4: sync de CSC com a Nuvem Fiscal aposentado — o CSC
      // operacional vive no acbrlib.ini do tenant na VPS (D-142.6).

      return savedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-settings'] });
      toast({
        title: 'Configurações salvas',
        description: 'As configurações de notas fiscais foram atualizadas com sucesso.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao salvar',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const uploadCertificate = async (file: File) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Usuário não autenticado');

    const { data: profile } = await supabase
      .from('profiles')
      .select('company_id')
      .eq('user_id', user.id)
      .single();

    if (!profile?.company_id) throw new Error('Empresa não encontrada');

    const fileName = `${profile.company_id}/${Date.now()}_${file.name}`;
    
    const { data, error } = await supabase.storage
      .from('certificates')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) throw error;

    const { data: { publicUrl } } = supabase.storage
      .from('certificates')
      .getPublicUrl(fileName);

    return publicUrl;
  };

  return {
    settings,
    isLoading,
    updateSettings: updateSettings.mutateAsync,
    uploadCertificate,
  };
};
